import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

import Home from "@/pages/Home";
import Apply from "@/pages/Apply";
import Resources from "@/pages/Resources";
import FollowUpSystem from "@/pages/FollowUpSystem";
import SalesMastery from "@/pages/SalesMastery";
import Playbooks from "@/pages/Playbooks";
import PlaybookDetail from "@/pages/PlaybookDetail";
import Library from "@/pages/Library";
import Stack from "@/pages/Stack";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResourceDetail from "@/pages/ResourceDetail";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Community from "@/pages/Community";
import CommunitySpace from "@/pages/CommunitySpace";
import PostDetail from "@/pages/PostDetail";
import Tools from "@/pages/Tools";
import { GrowthPacks, GrowthPackDetail } from "@/pages/GrowthPacks";
import { ProblemSolver, ProblemDetail } from "@/pages/ProblemSolver";
import Requests from "@/pages/Requests";
import LiveSessions from "@/pages/LiveSessions";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Podcast from "@/pages/Podcast";
import Learn from "@/pages/Learn";
import LearnEpisode from "@/pages/learn/LearnEpisode";
import LearnCarousel from "@/pages/learn/LearnCarousel";
import LearnGuide from "@/pages/learn/LearnGuide";
import PodcastEpisode from "@/pages/PodcastEpisode";
import Daily from "@/pages/Daily";
import Legal from "@/pages/Legal";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import Saved from "@/pages/Saved";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import NotFound from "@/pages/NotFound";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

function LegacyRoute({ children }) {
  const { data, isLoading } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/config")).data, staleTime: 300000 });
  if (isLoading) return null;
  return data?.legacy_sections_enabled ? children : <Navigate to="/" replace />;
}

function LegacyOfferRoute({ children }) {
  const { data, isLoading } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/config")).data, staleTime: 300000 });
  if (isLoading) return null;
  return data?.legacy_offers_enabled ? children : <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/sales-mastery" element={<LegacyOfferRoute><SalesMastery /></LegacyOfferRoute>} />
      <Route path="/playbooks" element={<LegacyOfferRoute><Playbooks /></LegacyOfferRoute>} />
      <Route path="/playbooks/:slug" element={<LegacyOfferRoute><PlaybookDetail /></LegacyOfferRoute>} />
      <Route path="/library" element={<LegacyOfferRoute><Library /></LegacyOfferRoute>} />
      <Route path="/stack" element={<Stack />} />
      <Route path="/table" element={<Navigate to="/" replace />} />
      <Route path="/table/apply" element={<Navigate to="/" replace />} />
      <Route path="/table/pay/:token" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<LegacyRoute><ResourceDetail /></LegacyRoute>} />
      <Route path="/courses" element={<Courses />} />
      <Route path="/courses/follow-up-system" element={<FollowUpSystem />} />
      <Route path="/courses/:slug" element={<CourseDetail />} />
      <Route path="/community" element={<LegacyRoute><ProtectedRoute><Community /></ProtectedRoute></LegacyRoute>} />
      <Route path="/community/:slug" element={<LegacyRoute><ProtectedRoute><CommunitySpace /></ProtectedRoute></LegacyRoute>} />
      <Route path="/community/post/:id" element={<LegacyRoute><ProtectedRoute><PostDetail /></ProtectedRoute></LegacyRoute>} />
      <Route path="/tools" element={<LegacyRoute><Tools /></LegacyRoute>} />
      <Route path="/packs" element={<LegacyRoute><GrowthPacks /></LegacyRoute>} />
      <Route path="/packs/:slug" element={<LegacyRoute><GrowthPackDetail /></LegacyRoute>} />
      <Route path="/problem-solver" element={<LegacyRoute><ProblemSolver /></LegacyRoute>} />
      <Route path="/problem-solver/:slug" element={<LegacyRoute><ProblemDetail /></LegacyRoute>} />
      <Route path="/requests" element={<LegacyRoute><ProtectedRoute><Requests /></ProtectedRoute></LegacyRoute>} />
      <Route path="/sessions" element={<LegacyRoute><ProtectedRoute><LiveSessions /></ProtectedRoute></LegacyRoute>} />
      <Route path="/pricing" element={<LegacyOfferRoute><Pricing /></LegacyOfferRoute>} />
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/d/:slug" element={<BlogPost />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/learn/ep/:slug" element={<LearnEpisode />} />
      <Route path="/learn/c/:slug" element={<LearnCarousel />} />
      <Route path="/learn/g/:slug" element={<LearnGuide />} />
      <Route path="/podcast" element={<Podcast />} />
      <Route path="/podcast/:slug" element={<PodcastEpisode />} />
      <Route path="/daily" element={<Daily />} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}
